import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { AlertTriangle, CheckCircle, AlertCircle, TrendingUp, Database } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DataQualityPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [siteSummaries, setSiteSummaries] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, sitesRes, flagsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/data-quality/dashboard`, { headers }),
        axios.get(`${BACKEND_URL}/api/data-quality/sites`, { headers }),
        axios.get(`${BACKEND_URL}/api/data-quality/flags?resolved=false&limit=50`, { headers }),
      ]);

      setDashboard(dashboardRes.data);
      setSiteSummaries(sitesRes.data);
      setFlags(flagsRes.data);
    } catch (error) {
      console.error('Failed to fetch data quality data:', error);
      toast.error('Failed to load data quality information');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveFlag = async (flagId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/data-quality/flags/${flagId}/resolve`,
        { resolution_notes: 'Resolved from dashboard' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Flag resolved');
      fetchData();
    } catch (error) {
      console.error('Failed to resolve flag:', error);
      toast.error('Failed to resolve flag');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 bg-red-50 border-red-200',
      high: 'text-orange-600 bg-orange-50 border-orange-200',
      medium: 'text-amber-600 bg-amber-50 border-amber-200',
      low: 'text-blue-600 bg-blue-50 border-blue-200',
    };
    return colors[severity] || 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const getReadinessColor = (readiness) => {
    const colors = {
      ready: 'text-green-600 bg-green-50',
      issues: 'text-amber-600 bg-amber-50',
      blocked: 'text-red-600 bg-red-50',
    };
    return colors[readiness] || 'text-slate-600 bg-slate-50';
  };

  const filteredFlags = selectedSeverity === 'all' 
    ? flags 
    : flags.filter(f => f.severity === selectedSeverity);

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <PageHeader
            title="Data Quality Monitor"
            description="Track data quality, identify issues, and maintain billing readiness"
          />

          {/* KPI Cards */}
          {dashboard && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-slate-600">Total Records</h3>
                    <Database className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{dashboard.total_records.toLocaleString()}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {dashboard.valid_records.toLocaleString()} valid
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-slate-600">Flagged Records</h3>
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-3xl font-bold text-amber-600">{dashboard.flagged_records}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    {((dashboard.flagged_records / dashboard.total_records) * 100).toFixed(1)}% of total
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-slate-600">Critical Issues</h3>
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <p className="text-3xl font-bold text-red-600">
                    {dashboard.issues_by_severity?.critical || 0}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {dashboard.missing_cumulative_discharge_count} missing billing data
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-slate-600">Billing Readiness</h3>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-green-600">
                    {dashboard.billing_readiness_score}%
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {dashboard.sites_with_issues.length} sites need attention
                  </p>
                </div>
              </div>

              {/* Issue Type Breakdown */}
              <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm mb-8">
                <h3 className="font-semibold text-slate-900 mb-4">Issue Type Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(dashboard.issues_by_type || {}).map(([type, count]) => (
                    <div key={type} className="text-center p-4 bg-slate-50 rounded-sm">
                      <p className="text-2xl font-bold text-slate-900">{count}</p>
                      <p className="text-sm text-slate-600 capitalize mt-1">{type}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Site Data Quality Summary */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm mb-8">
            <div className="p-6 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Site Data Quality</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Billing Readiness</th>
                    <th>Completeness</th>
                    <th>Total Flags</th>
                    <th>Critical</th>
                    <th>Unresolved</th>
                    <th>Latest Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {siteSummaries.map((site) => (
                    <tr key={site.site_id}>
                      <td className="font-medium text-slate-900">{site.site_name}</td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium capitalize ${getReadinessColor(site.billing_readiness)}`}>
                          {site.billing_readiness}
                        </span>
                      </td>
                      <td className="font-mono text-slate-900">{site.data_completeness_percent}%</td>
                      <td className="text-slate-600">{site.total_flags}</td>
                      <td className="text-red-600 font-medium">{site.critical_flags}</td>
                      <td className="text-amber-600 font-medium">{site.unresolved_flags}</td>
                      <td className="text-xs text-slate-600">
                        {site.latest_issues.length > 0 ? site.latest_issues[0] : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Active Quality Flags */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Active Quality Flags</h3>
              <div className="flex space-x-2">
                {['all', 'critical', 'high', 'medium', 'low'].map((severity) => (
                  <Button
                    key={severity}
                    size="sm"
                    variant={selectedSeverity === severity ? 'default' : 'outline'}
                    onClick={() => setSelectedSeverity(severity)}
                    className={selectedSeverity === severity ? 'bg-slate-900' : ''}
                  >
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            {filteredFlags.length === 0 ? (
              <EmptyState
                icon={CheckCircle}
                title="No quality issues"
                description="All data quality checks are passing"
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredFlags.map((flag) => (
                  <div key={flag.flag_id} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium border ${getSeverityColor(flag.severity)}`}>
                            {flag.severity.toUpperCase()}
                          </span>
                          <span className="text-sm font-medium text-slate-900">
                            {flag.field_name && `Field: ${flag.field_name}`}
                          </span>
                          <span className="text-xs text-slate-500 capitalize">
                            {flag.flag_type}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mb-2">{flag.description}</p>
                        <p className="text-xs text-slate-500">
                          Record: {flag.record_id.substring(0, 8)}... • {new Date(flag.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolveFlag(flag.flag_id)}
                        className="ml-4"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Resolve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
